import { useCallback, useEffect, useState } from 'react'
import ForgotPasswordLink from '../components/auth/ForgotPasswordLink'
import { useAuth } from '../contexts/AuthContext'
import {
    apiTelegramLoginConfig,
    apiTelegramLoginStart,
    apiTelegramBotLoginStart,
    apiTelegramBotLoginPoll,
    forgetTelegramBotLogin,
} from '../api/auth'
import { Shield, LogIn, Eye, EyeOff, AlertCircle, Loader2, Send, ExternalLink } from 'lucide-react'

// Причины отказа приходят кодом — человеку нужен русский текст и понятный
// следующий шаг: тупик на экране входа означает «не могу войти вообще».
const BOT_LOGIN_ERRORS_RU = {
    telegram_not_linked:
        'Этот Telegram не привязан ни к одной учётной записи. Войдите по паролю и привяжите Telegram в личном кабинете.',
    account_disabled: 'Учётная запись отключена. Обратитесь к администратору.',
    organization_suspended: 'Работа организации приостановлена. Обратитесь к администратору.',
    ambiguous_telegram_binding: 'Этот Telegram привязан к нескольким учётным записям. Обратитесь к администратору.',
}

export default function LoginPage() {
    const { login, error, adoptSession } = useAuth()
    const [form, setForm] = useState({ login: '', password: '' })
    const [showPassword, setShowPassword] = useState(false)
    const [loading, setLoading] = useState(false)
    const [localError, setLocalError] = useState(null)
    // Кнопку показываем только когда вход через Telegram реально настроен
    // (BotFather + переменные окружения). Иначе человек упрётся в 503.
    const [telegramEnabled, setTelegramEnabled] = useState(false)
    const [telegramLoading, setTelegramLoading] = useState(false)
    // Запасной вход через бота (ADR-058) — включается отдельно от OIDC: ему
    // не нужен Web Login в BotFather, достаточно живого бота.
    const [botLoginEnabled, setBotLoginEnabled] = useState(false)
    const [botLoginBusy, setBotLoginBusy] = useState(false)
    const [botRequest, setBotRequest] = useState(null)

    useEffect(() => {
        let alive = true
        apiTelegramLoginConfig()
            .then(cfg => {
                if (!alive) return
                setTelegramEnabled(!!cfg?.enabled)
                setBotLoginEnabled(!!cfg?.bot_login_enabled)
            })
            .catch(() => { /* недоступность справки — просто без кнопки */ })
        return () => { alive = false }
    }, [])

    const closeBotRequest = useCallback(() => {
        forgetTelegramBotLogin()
        setBotRequest(null)
    }, [])

    const handleBotLogin = async () => {
        setBotLoginBusy(true)
        setLocalError(null)
        try {
            const data = await apiTelegramBotLoginStart()
            setBotRequest({
                requestId: data.request_id,
                code: data.confirm_code,
                deepLink: data.deep_link,
                pollInterval: (data.poll_interval || 2) * 1000,
                expiresAt: Date.now() + (data.expires_in || 300) * 1000,
            })
            // Ссылку всё равно показываем кнопкой ниже: блокировщик всплывающих
            // окон вправе не пустить открытие после await, и тогда человек
            // остался бы перед экраном ожидания без способа попасть в бота.
            window.open(data.deep_link, '_blank', 'noopener,noreferrer')
        } catch (err) {
            setLocalError(err.message || 'Не удалось начать вход через бота')
        } finally {
            setBotLoginBusy(false)
        }
    }

    // Опрос заявки. Единственный источник правды о её судьбе — сервер: бот
    // подтверждает вход в другом приложении, никакого события в эту вкладку
    // не приходит.
    useEffect(() => {
        if (!botRequest) return
        let alive = true
        let timer = null

        const tick = async () => {
            if (!alive) return
            if (Date.now() > botRequest.expiresAt) {
                closeBotRequest()
                setLocalError('Заявка на вход истекла. Начните заново.')
                return
            }
            try {
                const res = await apiTelegramBotLoginPoll(botRequest.requestId)
                if (!alive) return
                if (res.status === 'approved') {
                    setBotRequest(null)
                    await adoptSession(res)
                    return
                }
                if (res.status === 'denied') {
                    closeBotRequest()
                    setLocalError('Вход отклонён в Telegram.')
                    return
                }
                if (res.status === 'error') {
                    closeBotRequest()
                    setLocalError(BOT_LOGIN_ERRORS_RU[res.reason] || 'Войти через бота не получилось.')
                    return
                }
                if (res.status === 'expired') {
                    closeBotRequest()
                    setLocalError('Заявка на вход истекла. Начните заново.')
                    return
                }
            } catch {
                // Сетевая заминка не должна убивать ожидание: следующий такт
                // спросит снова, а срок заявки закроет её сам.
            }
            if (alive) timer = setTimeout(tick, botRequest.pollInterval)
        }

        timer = setTimeout(tick, botRequest.pollInterval)
        return () => { alive = false; if (timer) clearTimeout(timer) }
    }, [botRequest, adoptSession, closeBotRequest])

    const handleTelegramLogin = async () => {
        setTelegramLoading(true)
        setLocalError(null)
        try {
            const { authorization_url } = await apiTelegramLoginStart()
            // Переходим в том же окне: новая вкладка потеряла бы секрет обмена,
            // он живёт в sessionStorage этой вкладки.
            window.location.href = authorization_url
        } catch (err) {
            setLocalError(err.message || 'Не удалось начать вход через Telegram')
            setTelegramLoading(false)
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!form.login || !form.password) {
            setLocalError('Введите логин и пароль')
            return
        }
        setLoading(true)
        setLocalError(null)
        try {
            await login(form.login, form.password)
        } catch (err) {
            setLocalError(err.message)
        } finally {
            setLoading(false)
        }
    }

    const errMsg = localError || error

    return (
        <div
            style={{
                minHeight: '100vh',
                background: 'var(--color-bg)',
                fontFamily: "'Inter', 'Plus Jakarta Sans', -apple-system, sans-serif"
            }}
            className='flex items-center justify-center'>
            {/* Background decoration */}
            <div
                style={{
                    position: 'fixed',
                    inset: 0,
                    pointerEvents: 'none'
                }}
                className='overflow-hidden'>
                <div
                    style={{
                        top: '-20%',
                        left: '-10%',
                        width: 600,
                        height: 600,
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, var(--color-accent-bg) 0%, transparent 70%)'
                    }}
                    className='absolute' />
                <div
                    style={{
                        bottom: '-20%',
                        right: '-10%',
                        width: 700,
                        height: 700,
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, color-mix(in srgb, var(--color-success) 6%, transparent) 0%, transparent 70%)'
                    }}
                    className='absolute' />
                <div
                    style={{
                        top: '30%',
                        right: '20%',
                        width: 400,
                        height: 400,
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, color-mix(in srgb, var(--color-purple) 5%, transparent) 0%, transparent 70%)'
                    }}
                    className='absolute' />
            </div>
            <div
                style={{
                    width: 420,
                    maxWidth: '95vw',
                    borderRadius: 24,
                    background: 'var(--color-bg-elevated)',
                    border: '1px solid var(--color-border)',
                    boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
                    zIndex: 'var(--z-content)'
                }}
                className='p-40 relative'>
                {/* Logo */}
                <div
                    style={{
                        marginBottom: 32
                    }}
                    className='text-center'>
                    <img
                        src="/favicon.svg"
                        alt="Onboardix"
                        width={64}
                        height={64}
                        style={{
                            display: 'block',
                            margin: '0 auto 16px',
                            borderRadius: 18,
                            boxShadow: '0 4px 20px color-mix(in srgb, var(--color-accent) 25%, transparent)'
                        }} />
                    <h1
                        style={{
                            margin: 0,
                            color: 'var(--color-text)',
                            letterSpacing: '-0.02em'
                        }}
                        className='text-4xl fw-800'>Onboardix</h1>
                    <p
                        style={{
                            color: 'var(--color-text-muted)'
                        }}
                        className='text-md mt-4'>HR · ERP · AI Platform</p>
                </div>

                {/* Error */}
                {errMsg && (
                    <div
                        style={{
                            padding: '10px 14px',
                            borderRadius: 'var(--radius-md)',
                            background: 'color-mix(in srgb, var(--color-danger) 8%, transparent)',
                            border: '1px solid color-mix(in srgb, var(--color-danger) 15%, transparent)',
                            color: 'var(--color-danger)'
                        }}
                        className='mb-16 flex items-center gap-8 text-md fw-600'>
                        <AlertCircle size={14} />
                        {errMsg}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="mb-16">
                        <label
                            style={{
                                color: 'var(--color-text-secondary)'
                            }}
                            className='text-md fw-600 block mb-6'>
                            Логин
                        </label>
                        <input
                            value={form.login}
                            onChange={e => setForm(f => ({ ...f, login: e.target.value }))}
                            placeholder="admin"
                            autoFocus
                            autoComplete="username"
                            style={{
                                padding: '12px 14px',
                                borderRadius: 'var(--radius-md)',
                                background: 'rgba(0,0,0,0.03)',
                                border: '1px solid rgba(0,0,0,0.08)',
                                color: 'var(--color-text)',
                                fontSize: '0.938rem',
                                outline: 'none',
                                fontFamily: 'inherit',
                                transition: 'border-color 0.2s, box-shadow 0.2s'
                            }}
                            onFocus={e => { e.target.style.borderColor = 'var(--color-accent)'; e.target.style.boxShadow = '0 0 0 3px var(--color-accent-bg)' }}
                            onBlur={e => { e.target.style.borderColor = 'rgba(0,0,0,0.08)'; e.target.style.boxShadow = 'none' }}
                            className='w-full' />
                    </div>

                    <div className="mb-24">
                        <label
                            style={{
                                color: 'var(--color-text-secondary)'
                            }}
                            className='text-md fw-600 block mb-6'>
                            Пароль
                        </label>
                        <div className="relative">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                value={form.password}
                                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                                placeholder="••••••"
                                autoComplete="current-password"
                                style={{
                                    padding: '12px 14px',
                                    paddingRight: 40,
                                    borderRadius: 'var(--radius-md)',
                                    background: 'rgba(0,0,0,0.03)',
                                    border: '1px solid rgba(0,0,0,0.08)',
                                    color: 'var(--color-text)',
                                    fontSize: '0.938rem',
                                    outline: 'none',
                                    fontFamily: 'inherit',
                                    transition: 'border-color 0.2s, box-shadow 0.2s'
                                }}
                                onFocus={e => { e.target.style.borderColor = 'var(--color-accent)'; e.target.style.boxShadow = '0 0 0 3px var(--color-accent-bg)' }}
                                onBlur={e => { e.target.style.borderColor = 'rgba(0,0,0,0.08)'; e.target.style.boxShadow = 'none' }}
                                className='w-full' />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                style={{
                                    right: 10,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    color: 'var(--color-text-muted)',
                                    padding: 4
                                }}
                                className='absolute'>
                                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            padding: '13px 20px',
                            borderRadius: 'var(--radius-lg)',
                            border: 'none',
                            background: loading ? 'var(--color-accent-hover)' : 'var(--color-accent)',
                            fontSize: '0.938rem',
                            cursor: loading ? 'default' : 'pointer',
                            boxShadow: '0 2px 12px color-mix(in srgb, var(--color-accent) 25%, transparent)',
                            fontFamily: 'inherit',
                            transition: 'all 0.2s',
                            opacity: loading ? 0.8 : 1
                        }}
                        className='w-full color-white fw-700 flex items-center justify-center gap-8'>
                        {loading ? (
                            <><Loader2 size={16} className='spin' /> Входим...</>
                        ) : (
                            <><LogIn size={16} /> Войти в систему</>
                        )}
                    </button>
                </form>

                {(telegramEnabled || botLoginEnabled) && (
                    <div className='flex items-center gap-8 mt-24 mb-16'>
                        <div style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
                        <span className='text-sm' style={{ color: 'var(--color-text-muted)' }}>или</span>
                        <div style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
                    </div>
                )}

                {telegramEnabled && (
                    <>
                        <button
                            type='button'
                            onClick={handleTelegramLogin}
                            disabled={telegramLoading}
                            style={{
                                padding: '13px 20px',
                                borderRadius: 'var(--radius-lg)',
                                border: '1px solid var(--color-border)',
                                background: 'var(--color-bg)',
                                color: 'var(--color-text)',
                                fontSize: '0.938rem',
                                cursor: telegramLoading ? 'default' : 'pointer',
                                fontFamily: 'inherit',
                                transition: 'all 0.2s'
                            }}
                            className='w-full fw-700 flex items-center justify-center gap-8'>
                            {telegramLoading ? (
                                <><Loader2 size={16} className='spin' /> Открываем Telegram…</>
                            ) : (
                                <><Send size={16} /> Войти через Telegram</>
                            )}
                        </button>
                    </>
                )}

                {botLoginEnabled && !botRequest && (
                    <button
                        type='button'
                        onClick={handleBotLogin}
                        disabled={botLoginBusy}
                        style={{
                            padding: '13px 20px',
                            marginTop: telegramEnabled ? 12 : 0,
                            borderRadius: 'var(--radius-lg)',
                            border: '1px solid var(--color-border)',
                            background: 'var(--color-bg)',
                            color: 'var(--color-text)',
                            fontSize: '0.938rem',
                            cursor: botLoginBusy ? 'default' : 'pointer',
                            fontFamily: 'inherit',
                            transition: 'all 0.2s'
                        }}
                        className='w-full fw-700 flex items-center justify-center gap-8'>
                        {botLoginBusy ? (
                            <><Loader2 size={16} className='spin' /> Готовим заявку…</>
                        ) : (
                            <><Send size={16} /> Войти по ссылке в боте</>
                        )}
                    </button>
                )}

                {botRequest && (
                    <div
                        style={{
                            marginTop: 16,
                            padding: 16,
                            borderRadius: 'var(--radius-lg)',
                            border: '1px solid var(--color-border)',
                            background: 'var(--color-bg-elevated)'
                        }}>
                        <div className='flex items-center gap-8 mb-8' style={{ color: 'var(--color-text-secondary)' }}>
                            <Loader2 size={14} className='spin' />
                            <span className='text-sm fw-700'>Ждём подтверждения в Telegram</span>
                        </div>
                        <p className='text-sm mb-12' style={{ color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
                            Откройте бота, сверьте код и нажмите «Это я». Код нужен, чтобы вы не подтвердили чужую заявку.
                        </p>
                        <div
                            style={{
                                padding: '10px 12px',
                                borderRadius: 'var(--radius-md)',
                                background: 'var(--color-accent-bg)',
                                letterSpacing: 6,
                                fontSize: '1.5rem',
                                textAlign: 'center',
                                color: 'var(--color-text)'
                            }}
                            className='fw-700 mb-12'>
                            {botRequest.code}
                        </div>
                        <a
                            href={botRequest.deepLink}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='btn btn--primary w-full flex items-center justify-center gap-8'>
                            <ExternalLink size={14} /> Открыть бота
                        </a>
                        <button
                            type='button'
                            onClick={closeBotRequest}
                            style={{
                                marginTop: 10,
                                background: 'none',
                                border: 'none',
                                color: 'var(--color-text-muted)',
                                cursor: 'pointer',
                                fontFamily: 'inherit'
                            }}
                            className='w-full text-sm'>
                            Отменить
                        </button>
                    </div>
                )}

                <ForgotPasswordLink defaultLogin={form.login} />

                <div
                    style={{
                        padding: '12px 14px',
                        borderRadius: 'var(--radius-md)',
                        background: 'color-mix(in srgb, var(--color-accent) 4%, transparent)',
                        border: '1px solid var(--color-accent-bg)',
                        color: 'var(--color-text-muted)',
                        lineHeight: 1.5
                    }}
                    className='mt-24 text-sm'>
                    <div
                        style={{
                            color: 'var(--color-text-secondary)'
                        }}
                        className='flex items-center gap-4 mb-4 fw-700'>
                        <Shield size={11} /> Учётные данные выдаются администратором
                    </div>
                    Для получения доступа обратитесь к IT-администратору или руководителю.
                </div>
            </div>
        </div>
    );
}
